import React, { useState } from "react";
import { Button, Alert } from "@mui/material";
import {
  statisticReassessment,
  conversionReassessment,
} from "../helpers/teamReassessment";
import { futureSummary } from "../helpers/teamAnalysis";
export default function FocusedStatisticGuide({
  data,
  teamId,
  statistic,
  en,
  explain,
  compact,
  busy,
  onPreviewStat,
  onApplyStat,
  statName,
  stat,
}) {
  const t = (d, e) => (en ? e : d),
    [pace, setPace] = useState(25),
    [field, setField] = useState("home_for");
  const conversion = statistic === "conversion",
    percent = conversion || String(statistic) === "45",
    scale = percent ? 100 : 1;
  const fmt = (v) =>
    Number.isFinite(v)
      ? (v * scale).toLocaleString(en ? "en-GB" : "de-DE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + (percent ? " %" : "")
      : "—";
  const rows = conversion
    ? conversionReassessment(data, teamId, pace)
    : statisticReassessment(data, teamId, pace).filter(
        (r) => String(r.statistic_id) === String(statistic)
      );
  const labels = {
    home_for: t("Heim-Offensive", "Home attack"),
    home_against: t("Heim-Defensive", "Home defence"),
    away_for: t("Auswärts-Offensive", "Away attack"),
    away_against: t("Auswärts-Defensive", "Away defence"),
  };
  const row = rows.find((r) => r.field === field);
  const prior = data.history?.teams?.find(
    (r) => Number(r.team_id) === Number(teamId)
  )?.sources?.[0];
  return (
    <section className="metric-statistic">
      <h2>{conversion ? "Conversion Rate" : statName(statistic)}</h2>
      <p>
        {conversion
          ? t(
              "Verwertung = Tore / Attack Moments. Offensive: eigene Verwertung; Defensive: zugelassene Verwertung. Kurzfristige Ergebnisse werden gedämpft berücksichtigt.",
              "Conversion = goals / Attack Moments. Attack: own conversion; defence: conversion conceded. Short-term results are damped."
            )
          : t(
              "Gespeicherte Eingabe und historische Erwartung sind unterschiedliche Größen. Der Anpassungsimpuls ist erspielt minus erwartet auf denselben Spielen.",
              "Saved input and historical expectation are different quantities. The adjustment signal is actual minus expected on the same matches."
            )}
      </p>
      {!compact && (
        <p>
          {conversion
            ? t(
                "Vorschlag = Ligamittel + 0,25 × (gespeichert − Ligamittel) + n/(n+40) × (erspielt − Ligamittel). n zählt belegte Spiele je Heim-/Auswärtskoordinate. Die Spitzenteamauswahl betrifft den Saisonwechsel und wird hier nicht nochmals verrechnet.",
                "Proposal = league mean + 0.25 × (saved − league mean) + n/(n+40) × (observed − league mean). n counts covered matches per home/away coordinate. Elite-team selection applies to season rollover and is not added again here."
              )
            : t(
                "Voller Zielwert = gespeichert + erspielt − erwartet. Der Regler bestimmt den Anteil dieser Abweichung. Die Vorschau berechnet die tatsächliche Wirkung auf Stärke und Torniveau einschließlich Liga-Normierung.",
                "Full target = saved + actual − expected. The slider selects the share of that difference. Preview calculates the actual effect on strength and goal level, including league normalization."
              )}
        </p>
      )}
      {!rows.length && (
        <Alert severity="info">
          {t(
            "Für diese Kennzahl sind keine gespeicherten Teamwerte vorhanden.",
            "No saved team values are available for this metric."
          )}
        </Alert>
      )}
      <div className="ta-scroll">
        <table>
          <thead>
            <tr>
              <th>{t("Wert", "Value")}</th>
              <th>{t("Gespeichert", "Saved")}</th>
              <th>{t("Erwartet", "Expected")}</th>
              <th>{t("Erspielt", "Actual")}</th>
              <th>
                {conversion
                  ? t("Ligamittel", "League mean")
                  : t("Vorsaison", "Previous season")}
              </th>
              <th>{t("Spiele", "Matches")}</th>
              {!explain && <th>{t("Vorschlag", "Proposal")}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.field}>
                <th>{labels[r.field]}</th>
                <td>
                  <b>{fmt(r.before)}</b>
                </td>
                <td>{fmt(r.range?.expected)}</td>
                <td>{fmt(r.range?.observed)}</td>
                <td>
                  {fmt(
                    conversion ? r.standard : r.previous?.mean ?? r.previous
                  )}
                </td>
                <td>{r.range?.count ?? 0}</td>
                {!explain && <td>{fmt(r.value)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!conversion && (
        <p>
          {t("Vorsaison", "Previous season")}:{" "}
          {prior
            ? `${prior.league_name || "—"} · ${prior.season_name || "—"}`
            : "—"}
          .{" "}
          {t(
            "Bei Ligawechsel nur Orientierung, kein direkter Zielwert.",
            "After a league change, context only, not a direct target."
          )}
        </p>
      )}
      {!explain && (
        <>
          <label>
            {t("Einzelwert auswählen", "Choose coordinate")}{" "}
            <select value={field} onChange={(e) => setField(e.target.value)}>
              {Object.entries(labels).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          {!conversion && (
            <label>
              {t("Anteil der Abweichung", "Share of the difference")}: {pace} %{" "}
              <input
                type="range"
                min="0"
                max="100"
                value={pace}
                onChange={(e) => setPace(+e.target.value)}
              />
            </label>
          )}
          {row && (
            <StatisticCandidate
              key={`${statistic}:${field}:${pace}:${row.before}`}
              {...{ row, teamId, scale, en, busy, onPreviewStat, onApplyStat }}
              current={
                conversion
                  ? stat?.teams?.[teamId]?.conversion?.[field]
                  : stat?.teams?.[teamId]?.statistics?.[statistic]?.[field]
              }
            />
          )}
        </>
      )}
    </section>
  );
}
function StatisticCandidate({
  row,
  teamId,
  scale,
  en,
  busy,
  onPreviewStat,
  onApplyStat,
  current,
}) {
  const t = (d, e) => (en ? e : d),
    [input, setInput] = useState(
      String(Number((row.value * scale).toFixed(3)))
    ),
    [touched, setTouched] = useState(false),
    [effect, setEffect] = useState(null),
    [pending, setPending] = useState(false),
    [error, setError] = useState(""),
    [applied, setApplied] = useState(false);
  const value = touched ? Number(input.replace(",", ".")) / scale : row.value;
  const candidate = {
    group: row.group,
    statistic_id: row.statistic_id,
    team_id: Number(teamId),
    field: row.field,
    value,
  };
  const fmt = (n) =>
    Number.isFinite(n)
      ? n.toLocaleString(en ? "en-GB" : "de-DE", { maximumFractionDigits: 3 })
      : "—";
  const inspect = async () => {
    setPending(true);
    setError("");
    try {
      setEffect(await onPreviewStat(candidate));
    } catch (e) {
      setError(e.message);
    } finally {
      setPending(false);
    }
  };
  const apply = async () => {
    setPending(true);
    setError("");
    try {
      if ((await onApplyStat(candidate)) !== false) {
        setApplied(true);
        setEffect(null);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setPending(false);
    }
  };
  const after = futureSummary(effect?.after) || effect?.after,
    before = futureSummary(effect?.before) || effect?.before;
  return (
    <div>
      <p>
        {t("Aktueller Entwurf", "Current draft")}: {fmt(current * scale)}
        {scale === 100 ? " %" : ""}
      </p>
      <label>
        {t("Vorschlag / eigener Wert", "Proposal / your value")}{" "}
        <input
          className="metric-input"
          inputMode="decimal"
          value={input}
          disabled={busy || pending}
          onChange={(e) => {
            setInput(e.target.value);
            setTouched(true);
            setEffect(null);
            setApplied(false);
          }}
        />
      </label>
      <Button
        disabled={
          busy || pending || !input || !Number.isFinite(value) || value < 0
        }
        onClick={inspect}
      >
        {t("Auswirkung prüfen", "Inspect effect")}
      </Button>
      {effect && (
        <p>
          {t("Spielstärke", "Strength")}: {fmt(before?.total_strength)} →{" "}
          {fmt(after?.total_strength)} · {t("Torniveau", "Goal level")}:{" "}
          {fmt(before?.total_goals)} → {fmt(after?.total_goals)}
        </p>
      )}
      <Button disabled={busy || pending || !effect} onClick={apply}>
        {t("Als Entwurf übernehmen", "Apply as draft")}
      </Button>
      <Button
        onClick={() => {
          setEffect(null);
          setInput(String(Number((row.before * scale).toFixed(3))));
          setTouched(true);
          setApplied(false);
        }}
        disabled={busy || pending}
      >
        {t("Vorschlag verwerfen", "Discard proposal")}
      </Button>
      {applied && (
        <p role="status">
          {t(
            "Im Entwurf. Noch nicht gespeichert. Gesamte Vorschau und Speichern unten.",
            "In draft. Not saved. Full preview and Save below."
          )}
        </p>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </div>
  );
}
